import type { TreeGridApprovedHourDateGroupRow, TreeGridApprovedHourRow } from "../rows/types"
import type { HourReportAdminAction, HourReportDayAuditAdminAction } from "../../../hour-reports/api"
import { isTotalHoursRow, isHourReportRow, isDateGroupRow, getDateGroupRows } from "../rows/utils"

type HandlerParams = {
  grid: TGrid
  row: TreeGridApprovedHourDateGroupRow | TreeGridApprovedHourRow
  checked: boolean
  updateHourReportStatus: (args: { id: string; status: "open" | "submitted" }) => unknown
  performHourReportAdminAction: (args: { id: string; action: HourReportAdminAction }) => unknown
  performDayAuditAdminAction: (args: { days: string[]; action: HourReportDayAuditAdminAction }) => unknown
  setDaysReadyForCheck: (args: { days: string[]; status: "open" | "submitted"; notifyChecker?: boolean }) => unknown
  confirmAllReportsReadyForReview: (request: TotalReadyForReviewRequest) => void
}

export type TotalReadyForReviewRequest = {
  days: string[]
  reportCount: number
}

export const handleChangeReadyForCheck = (params: HandlerParams) => {
  const {
    row,
    checked,
    updateHourReportStatus,
    performHourReportAdminAction,
    performDayAuditAdminAction,
    setDaysReadyForCheck,
    confirmAllReportsReadyForReview,
  } = params
  const nextHourReportStatus = checked ? "submitted" : "open"
  const nextDayAuditStatus = checked ? "submitted" : "open"

  if (isHourReportRow(row)) {
    if (row.editableAs === "hour-report-admin") {
      return performHourReportAdminAction({
        id: row.id,
        action: checked ? "submit" : "unsubmit",
      })
    }

    return updateHourReportStatus({
      id: row.id,
      status: nextHourReportStatus,
    })
  }

  if (isDateGroupRow(row)) {
    if (row.canManageAsHourReportAdmin) {
      return performDayAuditAdminAction({
        days: [row.id],
        action: checked ? "submit" : "unsubmit",
      })
    }

    return setDaysReadyForCheck({
      days: [row.id],
      status: nextDayAuditStatus,
    })
  }

  if (isTotalHoursRow(row)) {
    const dateRows = getDateGroupRows(params.grid)
    const dateRowIds = dateRows.map((row) => row.id)
    const canManageAsAdmin = dateRows.some((row) => row.canManageAsHourReportAdmin)
    if (!dateRowIds.length) return

    if (canManageAsAdmin) {
      return performDayAuditAdminAction({
        days: dateRowIds,
        action: checked ? "submit" : "unsubmit",
      })
    }

    if (checked) {
      confirmAllReportsReadyForReview({
        days: dateRowIds,
        reportCount: dateRows.reduce((total, row) => total + getReportCount(row), 0),
      })
      return 0
    }

    return setDaysReadyForCheck({
      days: dateRowIds,
      status: nextDayAuditStatus,
    })
  }
}

const getReportCount = (row: TreeGridApprovedHourDateGroupRow) => {
  const treeGridRow = row as TreeGridApprovedHourDateGroupRow & { childNodes?: unknown[] }
  return treeGridRow.Items?.length ?? treeGridRow.childNodes?.length ?? 0
}
